import { useState } from "react";
import { Input } from "@/components/ui/input";
import { X, Plus, Hash } from "@phosphor-icons/react";

/**
 * Compact per-line "Serial / Reference No." editor.
 *
 * Usage:
 *   <SerialEditor serials={row.serials} onChange={(next) => setRow(next)} />
 *
 * - Optional field; blank state renders a single "+ Add serial" pill.
 * - Multiple serials per line (typically ≤ qty, not enforced).
 * - `Enter` commits the current input; `Backspace` on empty input removes
 *   the last chip so power users can key in quickly.
 */
export default function SerialEditor({ serials = [], onChange, testidPrefix = "serial" }) {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    // Dedupe within a single row.
    if (serials.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...serials, v]);
    setDraft("");
  };

  const removeAt = (idx) => onChange(serials.filter((_, i) => i !== idx));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !draft && serials.length > 0) {
      onChange(serials.slice(0, -1));
    }
  };

  return (
    <div className="flex items-start gap-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center gap-1 pt-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500 whitespace-nowrap">
        <Hash size={11} weight="bold" />
        Serial / Ref
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-1">
        {serials.map((s, i) => (
          <span
            key={`${s}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-neutral-100 border border-neutral-300 text-[11px] font-mono"
            data-testid={`${testidPrefix}-chip-${i}`}
          >
            {s}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="hover:text-red-600"
              aria-label={`Remove ${s}`}
              data-testid={`${testidPrefix}-remove-${i}`}
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
        <div className="inline-flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            placeholder={serials.length ? "Add another…" : "Optional — press Enter after each"}
            className="rounded-none border-neutral-300 h-7 text-[11px] font-mono w-40"
            data-testid={`${testidPrefix}-input`}
          />
          {draft.trim() && (
            <button
              type="button"
              onClick={commit}
              className="h-7 px-2 border border-neutral-400 hover:bg-neutral-900 hover:text-white text-[11px] font-mono"
              data-testid={`${testidPrefix}-add-btn`}
            >
              <Plus size={11} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
